<template>
  <div>
    <b-modal
      size="xl"
      centered
      hide-footer
      header-class="head-modal"
      dialog-class="member-monthly-count-contract-modal"
      v-model="modalShow"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title mr-2">
           {{ data.year }}年 {{ getPeriodName(data.periodKey) }} {{ data.label }}的會員
        </h5>

        <convert-excel
          class="btn btn-success btn-sm"
          :data="excel_json.data"
          :fields="excel_json.fields"
          :worksheet="excel_json.worksheet"
          :name="excel_json.name"
          :type="excel_json.type"
        >
          下載
        </convert-excel>

        <button type="button" aria-label="Close" class="close" @click="close()">×</button>
      </template>

      <div class="container-fluid overflow-auto">
        <tables-page
          :tableData="table_data"
          :tableFields="table_fields"
          v-on:result="getMember"
        >
          <template #cell(join_date)="row">
            {{ convertRocDate(row.item.join_date, false) }}
          </template>

          <template #cell(dead_at)="row">
            {{ convertRocDate(row.item.dead_at, false) }}
          </template>

          <template #cell(exclusion_at)="row">
            {{ convertRocDate(row.item.exclusion_at, false) }}
          </template>

          <template #cell(accept_at)="row">
            {{ convertRocDate(row.item.accept_at, false) }}
          </template>

          <template #cell(seniority)="row">
            <template v-if="row.item.seniority_day >= 30">{{ row.item.seniority_month }}個月</template>
            <template v-else>{{ row.item.seniority_day }}天</template>
          </template>

          <template #cell(age)="row">
            {{ calculateAge(row.item.member_birthday) }}
          </template>
        </tables-page>
      </div>

      <b-overlay :show="status.isLoading" no-wrap></b-overlay>
    </b-modal>
  </div>
</template>

<script>
import TablesPage from "@/components/layouts/TablesPage";
import ConvertExcel from "@/components/common/ConvertExcel";
import contractApi from "@/models/API/contractApi";
import { convertRocDate, convertDateFormat, calculateAge, getDateRangeByYearAndPeriod } from "@/models/HELPERS/common.js";

export default {
  name: 'MemberMonthlyCountContractModal',
  model: {
    prop: 'show',
    event: 'change'
  },
  props: {
    show: {
      type: Boolean,
      required: false,
      default: false
    },
    // data 包含: year, periodKey (month_1, total...), rowType (add, dead...), label
    data: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  components: {
    TablesPage,
    ConvertExcel
  },
  data () {
    return {
      status: {
        isLoading: false
      },
      // 表格欄位定義
      table_fields: [
        { key: "no", label: "No" },
        { key: "sales_number", label: "業務編號", sortable: true },
        { key: "sales_name", label: "業務姓名", sortable: true },
        { key: "number", label: "契約編號", sortable: true },
        { key: "join_date", label: "入會日期", sortable: true },
        { key: "dead_at", label: "身故日期", sortable: true },
        { key: "accept_at", label: "受理日期", sortable: true },
        { key: "exclusion_at", label: "失效日期", sortable: true },
        { key: "exclusion_remark", label: "失效備註", sortable: true },
        { key: "member_ID_number", label: "互助人身分證", sortable: true },
        { key: "member_name", label: "互助人", sortable: true },
        { key: "pay_name", label: "要助人", sortable: true },
        { key: "seniority", label: "年資" },
        { key: "age", label: "年齡" }
      ],
      table_data: [],
      page: {},
      table_sort: {
        sortBy: '',
        sort: ''
      },
      excel_json: {
        fields: {},
        data: [],
        worksheet: 'My Worksheet',
        name: '每月件數統計明細.csv',
        type: 'csv'
      }
    }
  },
  computed: {
    modalShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('change', value);
      }
    },
  },
  watch: {
    show: {
      handler(newValue) {
        if (newValue) {
          this.getMember();
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.contractApi = new contractApi();
    this.load();
  },
  methods: {
    convertRocDate,
    calculateAge,

    load() {
      this.page = Object.assign({}, this.contractApi.page);
    },

    // 取得顯示用的週期名稱 (e.g. month_1 -> 1月)
    getPeriodName(key) {
        if(!key) return '';
        if(key === 'total') return '全年度合計';

        if(key.includes('season')) {
            const seasonMap = { season_1: '第一季', season_2: '第二季', season_3: '第三季', season_4: '第四季' };
            return seasonMap[key] || key;
        }

        if(key.includes('month')) {
            const monthStr = key.replace('month_', '');
            return `${monthStr}月`;
        }
        return key;
    },

    // 查詢契約主邏輯
    getMember(pageIndex = null, sortBy = '', sort = '') {
      this.status.isLoading = true;

      if (sortBy) this.table_sort.sortBy = sortBy;
      if (sort) this.table_sort.sort = sort;

      const { startDate, endDate } = getDateRangeByYearAndPeriod(this.data.year, this.data.periodKey);

      // 準備 API 參數
      let params = {
        'sortBy': this.table_sort.sortBy,
        'sort': this.table_sort.sort,
      };

      // 根據 rowType 決定要帶入哪個日期參數
      switch (this.data.rowType) {
        case 'add': // 新增件
          params['join_date_S'] = startDate;
          params['join_date_E'] = endDate;
          break;
        case 'dead': // 身故件
          params['dead_at_S'] = startDate;
          params['dead_at_E'] = endDate;
          break;
        case 'exclusion': // 失效件
          params['exclusion_at_S'] = startDate;
          params['exclusion_at_E'] = endDate;
          break;
        default:
          params['join_date_S'] = startDate;
          params['join_date_E'] = endDate;
          break;
    }

      this.contractApi.getMember((response) => {
        const res = response.data;

        // 加上序號 no
        this.table_data = res.data.map((item, index) => ({
            ...item,
            no: index + 1
        }));

        this.initCsvData();
        this.status.isLoading = false;
      }, params, false)
    },
    initCsvData() {
      // 1. 產生 header 對照: { "業務編號": "sales_number", ... }
      const fields = this.table_fields.reduce((acc, curr) => {
        // Excel 套件通常依賴這裡的 Value (curr.key) 去對應 data 裡的屬性
        acc[curr.label] = curr.key;
        return acc;
      }, {});

      // 2. 轉換資料內容
      const data = this.table_data.map(item => {
        let row = {};

        this.table_fields.forEach(field => {
          const key = field.key;
          const value = item[key];

          // 針對不同欄位做格式處理
          if (['join_date', 'dead_at', 'accept_at', 'exclusion_at'].includes(key)) {
            // 日期格式化 (根據你的 table 模板，我補上了 exclusion_at)
            row[key] = value ? convertDateFormat(value, false) : '';
          }
          else if (key === 'seniority') {
            // 年資處理
            const showMonth = item.seniority_day >= 30;
            row[key] = showMonth ? `${item.seniority_month}個月` : `${item.seniority_day}天`;
          }
          else if (key === 'age') {
            // 年齡計算 (因為 API 回傳通常只有生日，需要計算)
            row[key] = item.member_birthday ? calculateAge(item.member_birthday) : '';
          }
          else {
            // 一般欄位直接賦值，避免 null 顯示空白
            row[key] = (value !== null && value !== undefined) ? value : '';
          }
        });
        return row;
      });

      this.excel_json.fields = fields;
      this.excel_json.data = data;
    },
  }
}
</script>

<style lang="scss" scoped>
/deep/ .member-monthly-count-contract-modal {
  max-width: 1440px !important;
}
</style>



// WEBPACK FOOTER //
// src/views/Backstage/yf/components/modal/MemberMonthlyCountContractModal.vue